import { Card } from 'nhsuk-react-components';
import { Link } from 'react-router-dom';
import { YYYYMMDDToHumanReadable } from 'utils/formattingUtils';
import { Iteration } from 'schemas/Iteration.schema';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import useCampaign from 'hooks/useCampaign.ts';
import IterationStatus from 'components/IterationStatus';
import useAuth from 'hooks/useAuth.ts';

interface IterationPageHeaderProps {
    iteration: Iteration,
    title: string,
    showTitleAndLink?: boolean,
    showCopyLink?: boolean,
    actualRunTime?: string,
}

function IterationPageHeader({
  iteration, title,
  showTitleAndLink = true,
  showCopyLink = false,
  actualRunTime,
}: IterationPageHeaderProps) {
  const { getCampaignPathFor } = useCampaignNavigate();
  const { getDynamoIterationDetails } = useCampaign();
  const { user } = useAuth();
  const iterationDetailsNew = getDynamoIterationDetails(iteration.ID as string);

  const isLockedByMe = iterationDetailsNew?.locked_by?.id === user.userId;

  return (
    <Card>
      <>
        <h1 role="main" id="maincontent" className="nhsuk-heading-l nhsuk-u-margin-top-0 nhsuk-u-margin-bottom-3">
          {showTitleAndLink ? title : iteration.Name}
        </h1>

        {showTitleAndLink && iteration.Name && (
          <p className="nhsuk-u-margin-bottom-0">
            Iteration :
            {' '}
            <Link to={getCampaignPathFor(`iteration/${iteration.ID}/summary`)}>{iteration.Name}</Link>
          </p>
        )}

        {iterationDetailsNew?.status && (
          <p className="nhsuk-u-margin-bottom-1">
            Status:
            {' '}
            {iterationDetailsNew.locked_by?.id && (
            <img
              className="nhsuk-u-margin-bottom-0"
              src="/locked.svg"
              alt={`Iteration locked by ${iterationDetailsNew.locked_by.name}`}
              title={`Iteration locked by ${iterationDetailsNew.locked_by.name}`}
              style={
                    isLockedByMe
                      ? { filter: 'invert(25%) sepia(100%) saturate(1500%) hue-rotate(190deg) brightness(75%) contrast(120%)' }
                      : undefined
                  }
            />
            )}
            {' '}
            <IterationStatus iterationId={iteration.ID as string} />
          </p>
        )}
        {
            iteration.IterationDate && (
            <p className="nhsuk-u-margin-bottom-1">
              Planned run date :
              {' '}
              {YYYYMMDDToHumanReadable(iteration.IterationDate)}
            </p>
            )
        }

        {actualRunTime && (
          <p className="nhsuk-u-margin-bottom-1">
            Actual run date :
            {' '}
            {new Date(actualRunTime).toLocaleString('en-GB', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}

        {showCopyLink && (
          <p className="nhsuk-body-s">
            <Link
              to={getCampaignPathFor(`iteration/${iteration.ID}/copy`)}
              aria-label="Create a copy"
              data-testid="create-a-copy"
            >
              Create a copy
            </Link>
          </p>
        )}
      </>
    </Card>
  );
}

export default IterationPageHeader;
